import { DatePipe } from '@angular/common'
import { Component } from '@angular/core'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faFile, faFileExcel, faFileImage, faFilePdf, faFileWord } from '@fortawesome/free-solid-svg-icons'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'

import { BaseComponent, FeatureType, ModalSize } from '../../shared'
import { AttachmentPreviewModalComponent } from './attachment-preview-modal.component'
import { VoiceDictationModalComponent } from './voice-dictation-modal.component'

interface OutlookAttachmentInfo {
    id: string
    name: string
}

interface OutlookMailMessage {
    messageId: string
    subject: string
    receivedDateTime: string
    attachmentCount: number
    attachments: OutlookAttachmentInfo[]
}

interface AttachmentDownloadResult {
    previewUrl: string
    fileName: string
    contentType: string
}

interface VistaAutomationTestResult {
    path: string
    count: number
    directories: string[]
}

@Component({
    templateUrl: './actions.component.html',
    standalone: true,
    imports: [DatePipe, FaIconComponent],
})
export class ActionsComponent extends BaseComponent {
    cacheCreatedDate?: Date
    cacheExpirationDate?: Date
    outlookTestResults: OutlookMailMessage[] = []
    outlookTestError = ''
    outlookTestRan = false

    vistaTestDirectories: string[] = []
    vistaTestPath = ''
    vistaTestError = ''
    vistaTestRan = false

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.UsersEdit])
    }

    constructor() {
        super()
        this.refreshCacheDates()
    }

    refreshCacheDates() {
        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.get('/api/Action/GetLatestCacheDate')).subscribe({
                next: result => {
                    this.cacheCreatedDate = result[0].cacheCreatedDate
                    this.cacheExpirationDate = result[0].cacheExpirationDate
                },
            }),
        )
    }

    slackTest() {
        this.services.data.http.get('api/Action/SlackAutomationTest').subscribe(
            result => {
                console.log(result)
            },
            error => console.log(error),
        )
    }

    clearCache() {
        this.services.data.http.get('api/Action/ResetCache').subscribe(
            result => {
                this.services.notification.success('The Vista cache was cleared successfully')
                console.log(result)
                this.refreshCacheDates()
            },
            error => {
                this.services.notification.error('Failed to clear the Vista cache')
                console.log(error)
            },
        )
    }

    syncBidAnalysisLookups() {
        this.services.data.load(
            true,
            false,
            this.services.data.http.post<{ message: string; count: number }>('api/Action/SyncBidAnalysisLookups', {}),
        ).subscribe({
            next: results => {
                const body = results[0] as { message: string; count: number }
                this.services.notification.success(body?.message ?? `Bid analysis lookups updated. Synced ${body?.count} firms.`)
            },
            error: err => {
                const msg = err.error?.message ?? err.error?.error ?? 'Failed to update bid analysis lookups.'
                this.services.notification.error(msg)
            },
        })
    }

    syncVpPhaseActivityLookup() {
        this.services.data.load(
            true,
            false,
            this.services.data.http.post<{ message: string; count: number }>('api/Action/SyncVpPhaseActivityLookup', {}),
        ).subscribe({
            next: results => {
                const body = results[0] as { message: string; count: number }
                this.services.notification.success(
                    body?.message ?? `Vp phase activity lookup updated. Synced ${body?.count ?? 0} activities.`,
                )
            },
            error: err => {
                const msg = err.error?.message ?? err.error?.error ?? 'Failed to sync Vista activity codes.'
                this.services.notification.error(msg)
            },
        })
    }

    /** Calls a test endpoint that always returns 401 to verify session-expired redirect and sign-in message. */
    testSessionExpiredRedirect() {
        this.services.data.http.get('api/Action/TestSessionExpired').subscribe({
            next: () => {
                this.services.notification.info('Unexpected: server returned 200')
            },
            error: () => {
                // Interceptor handles 401: toast + redirect to signin?sessionExpired=true
            },
        })
    }

    vistaAutomationTest() {
        this.vistaTestDirectories = []
        this.vistaTestPath = ''
        this.vistaTestError = ''
        this.vistaTestRan = false

        this.services.data.load(
            true,
            false,
            this.services.data.http.get<VistaAutomationTestResult>('api/Action/VistaAutomationTest'),
        ).subscribe({
            next: results => {
                const body = results[0] as VistaAutomationTestResult
                this.vistaTestDirectories = body?.directories ?? []
                this.vistaTestPath = body?.path ?? ''
                this.vistaTestRan = true
            },
            error: err => {
                this.vistaTestError =
                    err.error?.message || err.error?.error || err.message || 'Unknown error reaching the Viewpoint Repository share'
                this.vistaTestRan = true
            },
        })
    }

    openVoiceDictation() {
        this.services.modals.open(VoiceDictationModalComponent, ModalSize.large)
    }

    onEstimateFileSelected(event: Event) {
        const input = event.target as HTMLInputElement
        if (input.files && input.files.length > 0) {
            const file = input.files[0]
            this.uploadEstimateFile(file)
            // Clear the input so the same file can be selected again
            input.value = ''
        }
    }

    onHcssActivityFileSelected(event: Event) {
        const input = event.target as HTMLInputElement
        if (input.files && input.files.length > 0) {
            const file = input.files[0]
            this.uploadHcssActivityFile(file)
            // Clear the input so the same file can be selected again
            input.value = ''
        }
    }

    uploadEstimateFile(file: File) {
        const formData: FormData = new FormData()
        formData.append('file', file, file.name)

        this.services.data.load(true, false, this.services.data.post('/api/Action/ImportEstimates', formData)).subscribe({
            next: () => {
                this.services.notification.success('Estimates imported successfully')
            },
            error: errorResult => {
                const errorMessage = errorResult.error?.message || errorResult.error || 'Failed to import estimates'
                this.services.notification.error('Failed to import estimates. ' + errorMessage)
            },
        })
    }

    uploadHcssActivityFile(file: File) {
        const formData: FormData = new FormData()
        formData.append('file', file, file.name)

        this.services.data.load(true, false, this.services.data.post('/api/Action/ImportHcssActivities', formData)).subscribe({
            next: () => {
                this.services.notification.success('HCSS Activities imported successfully')
            },
            error: errorResult => {
                const errorMessage = errorResult.error?.message || errorResult.error || 'Failed to import HCSS activities'
                this.services.notification.error('Failed to import HCSS activities. ' + errorMessage)
            },
        })
    }

    testOutlookEmail() {
        this.outlookTestResults = []
        this.outlookTestError = ''
        this.outlookTestRan = false

        this.services.data.load(
            true,
            false,
            this.services.data.http.get<OutlookMailMessage[]>('api/Action/TestOutlookEmail'),
        ).subscribe({
            next: results => {
                this.outlookTestResults = results[0] as OutlookMailMessage[]
                this.outlookTestRan = true
                this.services.notification.success(`Retrieved ${this.outlookTestResults.length} emails from VanConAP inbox`)
            },
            error: err => {
                this.outlookTestError = err.error?.message || err.error?.error || err.message || 'Unknown error connecting to Outlook'
                this.outlookTestRan = true
                this.services.notification.error('Failed to retrieve emails from Outlook')
            },
        })
    }

    getFileIcon(fileName: string): IconDefinition {
        const ext = this.getExtension(fileName)
        if (ext === '.pdf') return faFilePdf
        if (['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'].includes(ext)) return faFileImage
        if (['.xlsx', '.xls', '.csv'].includes(ext)) return faFileExcel
        if (['.docx', '.doc'].includes(ext)) return faFileWord
        return faFile
    }

    previewAttachment(messageId: string, attachmentId: string) {
        this.services.data.load(
            true,
            false,
            this.services.data.http.get<AttachmentDownloadResult>(
                `api/Action/DownloadOutlookAttachment?messageId=${encodeURIComponent(messageId)}&attachmentId=${encodeURIComponent(attachmentId)}`,
            ),
        ).subscribe({
            next: results => {
                const result = results[0] as AttachmentDownloadResult
                const ref = this.services.modals.open(AttachmentPreviewModalComponent, ModalSize.full95Percent)
                const modal = ref.componentInstance as AttachmentPreviewModalComponent
                modal.previewUrl = result.previewUrl
                modal.fileName = result.fileName
                modal.contentType = result.contentType
            },
            error: err => {
                const msg = err.error?.message || err.error?.error || 'Failed to download attachment'
                this.services.notification.error(msg)
            },
        })
    }

    private getExtension(name: string): string {
        const dot = name.lastIndexOf('.')
        return dot >= 0 ? name.substring(dot).toLowerCase() : ''
    }
}
